import { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { Button } from '@mui/material';
import { Grid } from '@mui/system';
import clsx from 'clsx';
import { isEmpty } from 'lodash';
import { MomentInput } from 'moment';
import { SYSTEM_EXPAND, USER_EXPAND } from 'common/actions/row-groups/types';
import {
  BALANCE_SHEET_NAME,
  BALANCE_SHEET_PROP,
  FINANCIAL_SLUG,
  HIDE_ADDITIONAL_HISTORICAL_YEARS,
  HIDE_BTN,
  INCOME_STATEMENT_PROP,
  INCOME_STATEMENT_SHEET_NAME,
  LTM_NTM_DATE_ROW_NUMBER,
  SHOW_ADDITIONAL_HISTORICAL_YEARS,
  SHOW_BTN,
} from 'common/constants/financials';
import { useFormat } from 'common/hooks';
import { useStore } from 'common/store';
import { EXPORT_MODE_STYLES } from 'common/types/pdfExport/constants';
import { AllocationFinalMessage, CustomCompactContainer, CustomContainer } from 'components';
import ScalarSpreadsheet from 'components/ScalarSpreadsheet';
import useWorkbook from 'components/ScalarSpreadsheet/utilities/useWorkbook';
import WorkbookContext from 'components/ScalarSpreadsheet/utilities/WorkbookContext';
import { useFinancials } from 'context/FinancialsContext';
import { useLayoutContext } from 'context/LayoutContext';
import { CellValue, ScalarSpreadsheetConfigDataInterface } from 'models/interfaces/spreadsheet-types/core-types';
import { rowConfig as balanceSheetRowConfig, template as balanceTemplate } from 'pages/Financials/balance-sheet/data';
import FinancialsDateSelector from 'pages/Financials/components/financials-date-selector';
import { FinancialsButtons, FinancialsNameDialog, FinancialsVersionDialog } from 'pages/Financials/components/index';
import {
  rowConfig as incomeStatementRowConfig,
  template as incomeTemplate,
} from 'pages/Financials/income-statement/data';
import KpiTable from 'pages/Financials/kpi/components/KpiTable';
import usePerformanceMetricsData from 'pages/Financials/performance-metrics/usePerformanceMetricsData';
import {
  getVisibleColumnsFromPeriods,
  useFinancialsPageHelpers,
  useIncomeStatementOrBalanceSheetData,
  useKpi,
} from 'pages/Financials/utilities';
import { PdfExportTargets } from 'pages/Valuations/util/constants';
import { useKeyEventForFabButton } from 'services/hooks';
import theme from 'theme';
import { dbShortDate, isValidDate } from 'utilities';
import { FinancialsTablesProps } from './FinancialsTables.props';
import { classes, StyledGridSkeleton } from './FinancialsTables.styles';
import { FinancialStatementPeriod } from '../../../../common/types/financials';
import { UseStoreValues } from '../../../../common/types/store';
import { SpreadsheetConfig } from '../../../../components/ScalarSpreadsheet/utilities/SpreadsheetConfig';
import FinancialsWidgetDrawer from '../../financials-widget-drawer/FinancialsWidgetDrawer';
import { KpiConfigurationRow } from '../../kpi/config/types';
import { FinancialsCell, FinancialTableData } from '../../utilities/types';
import { Config } from '../../utilities/use-income-or-balance-sheet/useIncomeStatementOrBalanceSheetData.props';

const sharedClass = 'financial-table';

const FINANCIALS_CONTAINER_CLASS = `${FINANCIAL_SLUG}-container`;
const FINANCIALS_INCOME_STATEMENT_CONTAINER_CLASS = `${FINANCIAL_SLUG}-income-statement-container`;
const FINANCIALS_PERFORMANCE_METRICS_CONTAINER_CLASS = `${FINANCIAL_SLUG}-performance-metrics-container`;
const FINANCIALS_BALANCE_SHEET_CONTAINER_CLASS = `${FINANCIAL_SLUG}-balance-sheet-container`;
const FINANCIALS_KPI_CONTAINER_CLASS = `${FINANCIAL_SLUG}-kpi-container`;

const FinancialsTables = ({
  additionalHistoricalYears,
  changePeriodDate,
  companyMeasurementDate,
  createVersion,
  fieldAttributes,
  filesParams,
  financialStatement,
  fiscalYearData,
  getVersions,
  measurementDate,
  measurementDates,
  notesParams,
  periods,
  selectedVersion,
  setAdditionalHistoricalYears,
  setFinancialStatement,
  setShowAdditionalHistoricalYears,
  showAdditionalHistoricalYears,
  currentFiscalYearEnd,
  setIsDisplayingRowNumbers,
  isAllocationFinal,
  isDisabled,
  setIsDisabled,
  viewOnlyUser,
  widgetDrawerProps,
}: FinancialsTablesProps) => {
  const [{ companyInfo }] = useStore() as unknown as UseStoreValues;
  const [format, formatDispatch] = useFormat();

  const { isExportMode } = useLayoutContext();

  const { useAdjustedEBITDA, setBalanceSheetsData, setIncomeStatementData } = useFinancials();

  // Income Statement Data
  const incomeStatementConfig = useMemo(() => incomeStatementRowConfig(isDisabled), [isDisabled]);

  const {
    spreadsheet: incomeStatementSheet,
    rowGroups: incomeStatementRowGroups,
    setRowGroups: setIncomeStatementRowGroups,
    setProgrammaticUpdate: incomeStatementSetProgrammaticUpdate,
  } = useIncomeStatementOrBalanceSheetData({
    fieldAttributes,
    isBalanceSheet: false,
    measurementDate,
    currentFiscalYearEnd,
    periods,
    setData: setIncomeStatementData,
    config: incomeStatementConfig,
    customTitlesData: {
      className: 'spreadsheet-table-title-financials',
      value: 'Income Statement',
    },
    tableTermsData: {
      columnName: 'Income Statement',
      pluralColumnName: 'Income Statements',
      tableName: 'Income Statement',
      tableSlug: 'income-statement',
    },
    useAdjustedEBITDA,
    isDisabled,
  });

  const { spreadsheet: performanceMetrics, rowGroups: performanceMetricsRowGroups, setRowGroups: setPerformanceMetricsRowGroups } = usePerformanceMetricsData({
    periods,
    fiscalYearData,
    isDisabled,
    incomeStatementSheet,
  });

  // BalanceSheet Data
  const balanceSheetConfig = useMemo(() => balanceSheetRowConfig(isDisabled), [isDisabled]);
  const {
    spreadsheet: balanceSheet,
    rowGroups,
    setRowGroups,
    setProgrammaticUpdate: balanceSheetSetProgrammaticUpdate,
  } = useIncomeStatementOrBalanceSheetData({
    fieldAttributes,
    isBalanceSheet: true,
    measurementDate,
    currentFiscalYearEnd,
    periods,
    setData: setBalanceSheetsData,
    config: balanceSheetConfig as unknown as Config[],
    customTitlesData: {
      className: 'spreadsheet-table-title',
      value: 'Balance Sheet',
    },
    tableTermsData: {
      columnName: 'Balance Sheet',
      pluralColumnName: 'Balance Sheet',
      tableName: 'Balance Sheet',
      tableSlug: 'balance-sheet',
    },
    isDisabled,
  });

  const hasExpandedRowGroup = useMemo(
    () => Object.values(rowGroups).some(rowGroup => [SYSTEM_EXPAND, USER_EXPAND].includes(rowGroup)),
    [rowGroups]
  );

  const { spreadsheet: kpiSheet, setTableData: setTableKpiData } = useKpi({
    periods,
    fieldAttributes,
    isDisabled,
  });

  const [spreadsheets, setSpreadsheets] = useState<SpreadsheetConfig[]>([]);
  const [selectedDate, setSelectedDate] = useState<string>();

  useEffect(() => {
    if (financialStatement?.financials_date) {
      setSelectedDate(financialStatement.financials_date);
    } else if (measurementDate?.date) {
      setSelectedDate(measurementDate.date);
    }
  }, [financialStatement, measurementDate]);

  // Update financialStatement.financials_date when selectedDate changes
  // This ensures loadFinancialStatementData re-runs with the new date
  useEffect(() => {
    if (selectedDate && financialStatement && financialStatement.financials_date !== selectedDate) {
      setFinancialStatement({
        ...financialStatement,
        financials_date: selectedDate,
      });
    }
  // Omit financialStatement from deps to avoid circular updates
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, setFinancialStatement]);

  const sheetsLoaded = useMemo(
    () =>
      incomeStatementSheet?.yLength === performanceMetrics?.yLength &&
      balanceSheet &&
      incomeStatementSheet?.tableData?.measurementDate?.id === balanceSheet?.tableData?.measurementDate?.id &&
      !isEmpty(kpiSheet?.tableData),
    [incomeStatementSheet, performanceMetrics, balanceSheet, kpiSheet]
  );

  useEffect(() => {
    if (sheetsLoaded && incomeStatementSheet && performanceMetrics && balanceSheet && kpiSheet) {
      setSpreadsheets([incomeStatementSheet, performanceMetrics, balanceSheet, kpiSheet]);
    }
  }, [incomeStatementSheet, performanceMetrics, balanceSheet, sheetsLoaded, kpiSheet]);

  const { onChange, cells, data, workbook, evaluateSpreadsheets, workbookContextValue } = useWorkbook(spreadsheets);

  const {
    addFinancialYear,
    isNameDialogOpen,
    isNewVersionDialogOpen,
    saveAsNewVersion,
    saveStatementWithNewName,
    setIsNameDialogOpen,
    setOpenNewVersionDialog,
    showDeleteConfirmation,
    showOrHideEarliestHistoricalYears,
  } = useFinancialsPageHelpers({
    additionalHistoricalYears,
    companyMeasurementDate,
    createVersion,
    filesParams,
    financialStatement,
    getVersions,
    measurementDate,
    measurementDates,
    notesParams,
    periods,
    selectedVersion,
    setAdditionalHistoricalYears,
    setFinancialStatement,
    setShowAdditionalHistoricalYears,
    showAdditionalHistoricalYears,
    tableData: workbook as unknown as FinancialTableData,
    isDisabled,
    isAllocationFinal,
    selectedDate,
  });

  useKeyEventForFabButton(!(data && cells));

  const handleOnChange = useCallback(
    (cell: FinancialsCell, value: CellValue) => {
      if ((cell.isNTM || cell.isLTM) && isValidDate(value as MomentInput)) {
        const updateCellsSynchronization = (updatedColumnsParam: FinancialStatementPeriod[], originSheet: string) => {
          // column sync/desync now depends on which table the date changed
          spreadsheets.forEach(sheet => {
            if (originSheet === sheet.name) {
              sheet.reset({ columns: updatedColumnsParam } as any);
            }
          });
          evaluateSpreadsheets(spreadsheets);
        };
        // retain temp state from cells to columns
        spreadsheets.forEach((sheet: ScalarSpreadsheetConfigDataInterface) => {
          if (sheet.reverseParser) {
            const updatedColumns = sheet.reverseParse() as FinancialStatementPeriod[];
            if (updatedColumns?.length) {
              sheet.reset({ columns: updatedColumns });
            }
          }
        });

        const sameTableColumns: FinancialStatementPeriod[] = spreadsheets.find(
          sheet => sheet.name === cell.sheet.name
        ).columns;

        const updatedPeriods = changePeriodDate(
          cell.columnId as string,
          value as string,
          cell.sheet.name,
          sameTableColumns
        );
        const propsToUse = {
          [INCOME_STATEMENT_SHEET_NAME]: {
            accessedProperty: INCOME_STATEMENT_PROP,
            template: incomeTemplate,
          },
          [BALANCE_SHEET_NAME]: {
            accessedProperty: BALANCE_SHEET_PROP,
            template: balanceTemplate,
          },
        };

        onChange(cell, value);
        const updatedColumns = getVisibleColumnsFromPeriods({
          ...propsToUse[cell.sheet.name as keyof typeof propsToUse],
          periods: updatedPeriods,
        } as any);
        updateCellsSynchronization(updatedColumns, cell.sheet.name);
      } else if (cell.isEditableTitleCell) {
        const {
          sheet: { tableData: kpiTableData },
        } = cell;
        const { companyKPIs } = kpiTableData;
        const kpiToChange = companyKPIs.find((kpi: KpiConfigurationRow) => kpi.alias === cell.alias);
        kpiToChange.name = value;
        onChange(cell, value);
      } else {
        onChange(cell, value);
      }
    },
    [changePeriodDate, evaluateSpreadsheets, onChange, spreadsheets]
  );

  const columnMarkedAsUpdated = useCallback(() => {
    evaluateSpreadsheets(spreadsheets);
  }, [spreadsheets, evaluateSpreadsheets]);

  useEffect(() => {
    document.addEventListener('columnUpdated', columnMarkedAsUpdated);
    return () => document.removeEventListener('columnUpdated', columnMarkedAsUpdated);
  }, [columnMarkedAsUpdated]);

  const sharedProps = useMemo(
    () => ({
      workbook,
      onChange: handleOnChange,
      customDeleteConfirmation: true,
      deleteColumn: showDeleteConfirmation,
      page: 'financials',
      className: sharedClass,
      setIsDisplayingRowNumbers,
    }),
    [handleOnChange, showDeleteConfirmation, workbook, setIsDisplayingRowNumbers]
  );

  const onScroll = (element: HTMLElement) => (event: Event) => {
    const scrollableContainers = document.querySelectorAll(
      `.${sharedClass}-scrollbar:not(#${(event.target as HTMLElement).id})`
    );

    scrollableContainers.forEach(scrollableItem => {
      let { scrollLeft } = element;

      if (element.scrollLeft + element.clientWidth === element.scrollWidth) {
        scrollLeft = scrollableItem.scrollWidth;
      }

      // eslint-disable-next-line no-param-reassign
      scrollableItem.scrollLeft = scrollLeft;
    });
  };

  useLayoutEffect(() => {
    const scrollAreas = document.querySelectorAll(`.${sharedClass}-scrollbar`);

    if (scrollAreas.length) {
      scrollAreas.forEach(scrollArea => {
        scrollArea.addEventListener('scroll', onScroll(scrollArea as HTMLElement));
      });
    }

    return () => {
      scrollAreas.forEach(scroll => {
        scroll.removeEventListener('scroll', onScroll(scroll as HTMLElement));
      });
    };
  }, []);

  const handleGlobalDateChange = useCallback(
    (date: string) => {
      const formattedDate = dbShortDate(date);
      setSelectedDate(date); // Store the selected date
      spreadsheets.forEach(sheet => {
        if (!sheet.cells) {
          return;
        }
        const ltmNtmDateCells = Object.values(sheet.cells).filter(
          (cell: any) => (cell.isLTM || cell.isNTM) && cell.rowNumber === LTM_NTM_DATE_ROW_NUMBER
        );
        ltmNtmDateCells.forEach((cell: any) => {
          handleOnChange(cell, formattedDate);
        });
      });
      evaluateSpreadsheets(spreadsheets);
    },
    [spreadsheets, handleOnChange, evaluateSpreadsheets]
  );

  if (
    incomeStatementSheet?.data &&
    performanceMetrics?.data &&
    balanceSheet?.data &&
    !isEmpty(kpiSheet?.cells) &&
    cells &&
    data &&
    showOrHideEarliestHistoricalYears &&
    financialStatement?.company_measurement_date === measurementDate?.cmd_id
  ) {
    (window as any).appGlobal = {
      ...(window as any).AppGlobal,
      allCells: cells as any,
    };
    return (
      <>
        <CustomCompactContainer mb={2} rowSpacing={2.5}>
          {isDisabled && isAllocationFinal && (
            <Grid size={{ xs: 12 }}>
              <AllocationFinalMessage
                variant
                tableTerms={{
                  tableName: 'Financials Table',
                }}
                setIsDisabled={setIsDisabled}
                finalAllocations={[]}
                savesAsProformaConfirmation={() => setOpenNewVersionDialog(true)}
                actions={[
                  {
                    label: 'Duplicate',
                    buttonProps: {
                      variant: 'contained',
                      size: 'small',
                      disabled: viewOnlyUser,
                    },
                    callback: () => setOpenNewVersionDialog(true),
                  },
                ]}
                viewOnlyUser={viewOnlyUser}
              />
            </Grid>
          )}

          <Grid size={{ xs: 12 }}>
            <FinancialsDateSelector
              onDateChange={handleGlobalDateChange}
              disabled={isDisabled}
              currentDate={selectedDate}
            />
          </Grid>
        </CustomCompactContainer>

        <WorkbookContext.Provider value={workbookContextValue}>
          <CustomContainer className={FINANCIALS_CONTAINER_CLASS} data-ref={PdfExportTargets.FINANCIALS.id} style={isExportMode ? {...EXPORT_MODE_STYLES} : {}}>
            <CustomCompactContainer style={isExportMode ? {...EXPORT_MODE_STYLES} : {}}>
              {/* Income Statement */}
              <Grid className={FINANCIALS_INCOME_STATEMENT_CONTAINER_CLASS} size={{ xs: 12 }} style={isExportMode ? EXPORT_MODE_STYLES : {}}>
                <ScalarSpreadsheet
                  {...incomeStatementSheet}
                  {...sharedProps}
                  rowGroups={incomeStatementRowGroups}
                  setRowGroups={setIncomeStatementRowGroups}
                  format={format}
                  formatDispatch={formatDispatch}
                  sheet={incomeStatementSheet}
                />
              </Grid>

              {/* Earliest historical years */}
              {!isExportMode && !isEmpty(additionalHistoricalYears) && (
                <Grid className={classes.additionalHistoricalYearsContent} pl={theme.spacing(1.5)} size={{ xs: 12 }}>
                  <span className={classes.additionalHistoricalYearsTxt}>
                    {!showAdditionalHistoricalYears
                      ? SHOW_ADDITIONAL_HISTORICAL_YEARS
                      : HIDE_ADDITIONAL_HISTORICAL_YEARS}
                  </span>

                  {additionalHistoricalYears?.map((item, idx) => {
                    const isLastItem = idx === additionalHistoricalYears?.length - 1;
                    return <span key={item.year}>{`${item.year}${!isLastItem ? ', ' : ''}`}</span>;
                  })}

                  <Button
                    color="primary"
                    onClick={() => showOrHideEarliestHistoricalYears()}
                    sx={{ display: 'inline-block', ml: 1, verticalAlign: 'baseline' }}>
                    {!showAdditionalHistoricalYears ? SHOW_BTN : HIDE_BTN}
                  </Button>
                </Grid>
              )}
            </CustomCompactContainer>

            {/* Performance Metrics */}
            <Grid className={FINANCIALS_PERFORMANCE_METRICS_CONTAINER_CLASS} size={{ xs: 12 }}>
              <ScalarSpreadsheet
                {...performanceMetrics}
                {...sharedProps}
                format={format}
                sheet={performanceMetrics}
                rowGroups={performanceMetricsRowGroups}
                setRowGroups={setPerformanceMetricsRowGroups}
              />
            </Grid>

            {/* Balance Sheet */}
            {!isExportMode && (
              <Grid
                className={clsx(FINANCIALS_BALANCE_SHEET_CONTAINER_CLASS, { expanded: hasExpandedRowGroup })}
                size={{ xs: 12 }}>
                <ScalarSpreadsheet
                  {...balanceSheet}
                  {...sharedProps}
                  format={format}
                  formatDispatch={formatDispatch}
                  rowGroups={rowGroups}
                  setRowGroups={setRowGroups}
                  sheet={balanceSheet}
                />
              </Grid>
            )}

            {/* KPI */}
            {!isExportMode && (
              <Grid className={FINANCIALS_KPI_CONTAINER_CLASS} size={{ xs: 12 }}>
                <KpiTable
                  kpiSheet={kpiSheet}
                  sharedProps={sharedProps as any}
                  setTableData={setTableKpiData}
                  isDisabled={isDisabled}
                />
              </Grid>
            )}
          </CustomContainer>

          <FinancialsButtons isDisabled={isDisabled} addFinancialYear={addFinancialYear} />

          <FinancialsNameDialog
            open={isNameDialogOpen}
            onClose={() => setIsNameDialogOpen(false)}
            onSave={saveStatementWithNewName}
            versionName={selectedVersion?.name ?? ''}
          />

          <FinancialsVersionDialog
            open={isNewVersionDialogOpen}
            onClose={() => setOpenNewVersionDialog(false)}
            onSave={saveAsNewVersion}
          />
          {workbook && (
            <FinancialsWidgetDrawer
              workbook={workbook}
              onTableChange={handleOnChange}
              applicationContext={widgetDrawerProps.applicationContext}
              documentReferencesProps={widgetDrawerProps.documentReferencesProps}
              notesProps={widgetDrawerProps.notesProps}
              spreadsheetNotesProps={widgetDrawerProps.spreadsheetNotesProps}
              documentApplicationStorageKey={`${companyInfo?.id}-${measurementDate.cmd_id}-${widgetDrawerProps.applicationContext}-doc-navigation-config`}
              incomeStatementSetProgrammaticUpdate={incomeStatementSetProgrammaticUpdate}
              balanceSheetSetProgrammaticUpdate={balanceSheetSetProgrammaticUpdate}
            />
          )}
        </WorkbookContext.Provider>
      </>
    );
  }

  return <StyledGridSkeleton />;
};

export default FinancialsTables;
